// Phase 7 R5 — Mandatory ticket-side procurement metadata.
//
// Resolves PO header (PO number + vendor name) for any parts_line that is
// linked to a purchase order line. Read-only. Uses one batched query so each
// ticket parts panel renders the metadata strip with a single roundtrip.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PartsLine } from '@/hooks/parts/usePartsLines';

export interface PartsLinePoContext {
  /** Linked PO line id (the join key on parts_lines). */
  purchase_order_line_id: string;
  purchase_order_id: string;
  po_number: string;
  vendor_name: string | null;
}

/**
 * Returns a map keyed by `purchase_order_line_id` so callers can look up
 * the PO context for any individual parts line in O(1).
 */
export function usePartsLinesPoContext(lines: PartsLine[] | undefined) {
  const linkedIds = Array.from(
    new Set(
      (lines ?? [])
        .map((l) => l.purchase_order_line_id)
        .filter((x): x is string => !!x),
    ),
  ).sort();

  return useQuery({
    queryKey: ['parts-lines-po-context', linkedIds.join(',')],
    enabled: linkedIds.length > 0,
    queryFn: async (): Promise<Map<string, PartsLinePoContext>> => {
      const { data, error } = await (supabase as any)
        .from('purchase_order_lines')
        .select('id, purchase_order_id, purchase_orders ( po_number, vendor_name )')
        .in('id', linkedIds);
      if (error) throw error;
      const map = new Map<string, PartsLinePoContext>();
      for (const row of (data ?? []) as any[]) {
        const po = row.purchase_orders;
        if (!po) continue;
        map.set(row.id as string, {
          purchase_order_line_id: row.id,
          purchase_order_id: row.purchase_order_id,
          po_number: po.po_number,
          vendor_name: po.vendor_name ?? null,
        });
      }
      return map;
    },
    staleTime: 30_000,
  });
}
